/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { numberToPercentageString } from 'src/core/utils/number';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { SummarizationCard } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/SummarizationCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { SummarizationResult } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { getEntityColor } from 'src/languageStudio/core/utils/demosUtils';
import { AnnotationData, Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type VisualizedSummarizationResultProps = {
    document: string;
    isLoading: boolean;
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    summarizationResult: SummarizationResult;
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    extractiveSummary: {
        id: 'languageStudio.visualizedSummarizationResult.extractiveSummary',
        defaultMessage: `Extractive Summary`
    },
    abstractiveSummary: {
        id: 'languageStudio.visualizedSummarizationResult.abstractiveSummary',
        defaultMessage: 'Abstractive Summary'
    },
    labelText: {
        id: 'languageStudio.visualizedSummarizationResult.labelText',
        defaultMessage: 'Rank Score'
    }
});

export const VisualizedSummarizationResult = (props: VisualizedSummarizationResultProps) => {
    const { summarizationResult, isLoading, isLabelerRTL, detectedLanguage, document, isLabelerWordTokenized } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();

    const extractiveStatus = summarizationResult?.extractive?.status;
    const isExtractiveLoading = isLoading && extractiveStatus !== 'succeeded';
    const abstractiveStatus = summarizationResult?.abstractive?.status;
    const isAbstractiveLoading = isLoading && abstractiveStatus !== 'succeeded';

    const extractiveSummarizationSentences =
        extractiveStatus === 'succeeded' ? summarizationResult?.extractive?.documents[0]?.sentences : null;
    const abstractiveSentences = abstractiveStatus === 'succeeded' ? summarizationResult?.abstractive?.documents[0]?.summaries : null;

    const annotations =
        extractiveSummarizationSentences?.map<AnnotationData>(e => ({
            id: getId(),
            kind: 'label',
            startToken: e.offset,
            color: getEntityColor('Rank'),
            endToken: e.offset + e.length - 1,
            name: `${localize(messages.labelText)}: ${numberToPercentageString({ ratio: e.rankScore })}`
        })) ?? [];

    return (
        <Stack styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 25 }}>
            <Stack
                horizontal={isLargeOrGreater}
                horizontalAlign="start"
                tokens={{ childrenGap: extractiveStatus && abstractiveStatus && isLargeOrGreater ? 70 : 30 }}>
                {(extractiveStatus || isExtractiveLoading) && (
                    <Stack tokens={{ childrenGap: 10 }} data-automation-id="extractiveSentencesCardContainer">
                        <LuisText fontWeight={500}>{localize(messages.extractiveSummary)}</LuisText>
                        <SummarizationCard
                            isOnlyCard={!(extractiveStatus && abstractiveStatus)}
                            isLoading={isExtractiveLoading}
                            sentences={extractiveSummarizationSentences}
                        />
                    </Stack>
                )}

                {(abstractiveStatus || isAbstractiveLoading) && (
                    <Stack tokens={{ childrenGap: 10 }} data-automation-id="abstractiveSentencesCardContainer">
                        <LuisText fontWeight={500}>{localize(messages.abstractiveSummary)}</LuisText>
                        <SummarizationCard
                            isExtractive={false}
                            isOnlyCard={!(extractiveStatus && abstractiveStatus)}
                            isLoading={isAbstractiveLoading}
                            sentences={abstractiveSentences}
                        />
                    </Stack>
                )}
            </Stack>

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    text={document}
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
